import * as React from 'react';
import { CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { Send } from '@mui/icons-material';
import { QueryClient, useMutation } from "@tanstack/react-query";
import { getOAuthAuthorizeUrl } from '../../controllers/accounts/auth';
import { createComment } from '../../controllers/posts/comments';
import { UserContext } from '../../context/UserContext';

const NAV = '#202c41';
const BG = '#F2F4F8';
const MUTED = '#8A95A3';

// A plain inline compose bar — no popup of its own. `id` is whoever the
// reply is currently targeting (the post itself, or a specific comment),
// owned by the parent (CommentDrawer/SlimCommentDrawer).
export default function CommentReplyDrawer({id, token, content, setContent, getAPIComments}) {
  const { userDetails } = React.useContext(UserContext)

  const queryClient = new QueryClient()
  const mutation = useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['createComment'] })
      getAPIComments()
      setContent("")
    },
  })

  const send_reply = () => {
    if (!userDetails?.id) {
      window.location.replace(getOAuthAuthorizeUrl())
      return
    }
    if (mutation.isPending || !content.trim()) return
    mutation.mutate({
      body:{
        status: content,
        in_reply_to_id: id,
        visibility:"unlisted"
      },
      token
    })
  }

  const canSend = content.trim().length > 0 && !mutation.isPending

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <TextField
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send_reply() } }}
          placeholder="Comment…"
          multiline
          maxRows={4}
          fullWidth
          size="small"
          disabled={mutation.isPending}
          sx={{
            background: BG,
            '& .MuiOutlinedInput-root': { borderRadius: 4, '& fieldset': { border: 'none' } },
          }}
        />
        <IconButton
          onClick={send_reply}
          disabled={!canSend}
          sx={{
            background: canSend ? NAV : BG,
            border: canSend ? 'none' : '1px solid #DDE4EF',
            color: canSend ? '#fff' : MUTED,
            width: 40, height: 40,
            '&:hover': { background: canSend ? '#16202f' : BG },
            '&.Mui-disabled': { color: MUTED },
          }}
        >
          {mutation.isPending ? <CircularProgress size={18} sx={{ color: MUTED }} /> : <Send sx={{ fontSize: 18 }} />}
        </IconButton>
      </div>
      {mutation.isError && (
        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
          Couldn't post your comment. Try again.
        </Typography>
      )}
    </div>
  );
}
